import type { StripeCardElementOptions } from "@stripe/stripe-js"
import { themeFromBrand } from "@ternary/ui"
import { useCallback, useState } from "react"
import { NEUTRAL_BRAND } from "../signup/neutral-brand.js"

// The light set's neutrals, which no brand changes: the card frame is light in every scheme.
const LIGHT = themeFromBrand(NEUTRAL_BRAND).light

/**
 * Stripe draws the card in its own iframe, which the page's CSS never reaches. Both card frames
 * (`.legacy-purchase-card` and `.legacy-payment-stripe-card`) are light in every colour scheme,
 * with the system canvas behind the system text colour (styles.css), so the text is the colour the
 * frame computes, dark on light, and the placeholder and invalid colours are the light set's.
 * A display option inside Stripe's iframe, never part of a request. As admin-web's cardOptions
 * (components/setup-page.tsx), with the same fallbacks.
 */
export function cardOptions(frame: Element): StripeCardElementOptions {
  const style = getComputedStyle(frame)
  const color = style.color || LIGHT.text
  return {
    style: {
      base: {
        "::placeholder": { color: LIGHT.muted },
        color,
        fontFamily: style.getPropertyValue("--font-sans").trim() || "sans-serif",
        fontSize: "16px",
        iconColor: color,
      },
      invalid: { color: LIGHT.danger },
    },
  }
}

/**
 * The ref for a card frame, and the options read from it once it is in place. Read once: the frame
 * is light under any scheme, so a scheme switch changes nothing they hold.
 */
export function useCardOptions() {
  const [options, setOptions] = useState<StripeCardElementOptions>()
  const frame = useCallback((node: HTMLElement | null) => {
    if (node) setOptions((current) => current ?? cardOptions(node))
  }, [])
  return [frame, options] as const
}
